import _ from 'lodash'
import moment from 'moment'
import { formatters } from '@adc/blueprint'
import {
  API_DATETIME_FORMAT,
  OFFER_DATE_TIME_FORMAT,
} from '../constants/statics'
import { conformToMask, phoneNumberMask } from './inputMasks'

/**
 * Formats a monetary value into a human-readable string.
 * Re-exports formatMoney from blueprint but maintains backward compatibility.
 *
 * @param val - The value to format. Can be a number, numeric string, null, or undefined.
 * @param overrides - Optional formatting overrides object. Pass `undefined` to use default formatting.
 * @param hideDecimals - Whether to hide decimal places. Defaults to false (shows decimals).
 * @param defaultValue - The value to return if val is null/undefined/invalid. Defaults to empty string.
 * @returns The formatted money string, or the defaultValue if formatting fails.
 *
 * @example
 * // Basic usage with defaults
 * formatMoney(1234.56) // Returns "$1,234.56"
 * formatMoney(1234.56, undefined, false, '') // Same as above, all defaults explicit
 *
 * // Hide decimals
 * formatMoney(1234.56, undefined, true) // Returns "$1,235"
 *
 * // Custom default for null/undefined
 * formatMoney(null, undefined, false, 'N/A') // Returns "N/A"
 */
export const formatMoney = (
  val: number | string | null | undefined,
  overrides?: Intl.NumberFormatOptions,
  hideDecimals = false,
  defaultValue = ''
) => {
  if (Number.isFinite(val)) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return formatters.formatMoney(val, {
      defaultValue,
      hideDecimals,
      overrides,
    })
  }

  if (typeof val === 'string') {
    const parsed = parseFloat(val)
    if (Number.isFinite(parsed) && parsed.toString() === val) {
      return val
    }
  }

  return val || defaultValue
}

export const formatTitleCase = (
  val: unknown,
  wordsToCapitalize: string[] = []
): string => {
  if (_.isNil(val)) return ''
  const titleCasedStr = _.startCase(_.lowerCase(String(val)))

  if (!wordsToCapitalize.length) {
    return titleCasedStr
  } else {
    const splitStr = titleCasedStr.split(' ')

    wordsToCapitalize.forEach(word => {
      const indexOfWord = splitStr.indexOf(_.startCase(word))

      if (indexOfWord !== -1) {
        splitStr[indexOfWord] = _.upperCase(word)
      }
    })

    return splitStr.join(' ')
  }
}

export const removeFileExtension = (
  fileName: string,
  extensionsToRemove: string[] = []
): string => {
  const removeSet = new Set(extensionsToRemove)
  const lastDot = fileName.lastIndexOf('.')
  if (lastDot === -1) return fileName
  const lastStr = fileName.substring(lastDot + 1)
  return removeSet.has(lastStr) ? fileName.substring(0, lastDot) : fileName
}

/**
 * @param d - datetime
 * @returns API date format (utc)
 */
export const dateToApiString = (d: moment.MomentInput): string =>
  moment(d).utc().format(API_DATETIME_FORMAT)

/**
 * Converts API (utc) string to local moment
 * @param s - API datetime string or other moment-compatible input
 * @returns moment in local mode
 */
export const apiStringToLocalMoment = (s: moment.MomentInput): moment.Moment =>
  moment.utc(s, API_DATETIME_FORMAT).local()

export const formatPhoneNumber = (value: string | number): string | number => {
  const digits = (value + '').replace(/[^\d]/g, '')

  // NOTE: This can cause weird results if QA randomized these numbers because it's possible to get numbers with
  // a leading 0, in which case `parseInt` will strip it from the result and cause this condition to fail and the
  // formatting not to be added
  return digits.length === 10 && digits === '' + parseInt(digits, 10)
    ? conformToMask(digits, phoneNumberMask).conformedValue
    : value
}

// This is useful if you want to render presentation states for an enum and want decent default behavior for if backend
// adds new values to the schema without the app updating at the same time
export const formatEnum = (enumVal: string | null | undefined): string =>
  (enumVal || '').split('_').map(_.capitalize).join(' ')

export const formatDate = (
  date: moment.MomentInput,
  format: string = OFFER_DATE_TIME_FORMAT
): string => apiStringToLocalMoment(date).format(format)

/**
 * Format a duration in milliseconds to a human-readable string.
 * <1s: "Xms", 1-60s: "X.Xs", >60s: "Xm Ys"
 */
export const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${Math.round(ms)}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  const minutes = Math.floor(ms / 60000)
  const seconds = Math.round((ms % 60000) / 1000)
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`
}

/**
 * Compute elapsed duration in ms between two timestamps.
 * Returns null if either value is missing or unparseable.
 */
export const computeElapsed = (
  startedAt?: string | null,
  completedDate?: unknown
): number | null => {
  if (!startedAt || !completedDate) return null
  const start = new Date(startedAt).getTime()
  const end = new Date(completedDate as string | number).getTime()
  if (isNaN(start) || isNaN(end)) return null
  return end - start
}
